import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import {
  Table,
  Button,
  Modal,
  Collapse,
  Spin,
  message,
  Form,
  Input,
  DatePicker,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { toPickerDate, isPickerDate } from '../utils/pickerDate';
import { useFirebase } from '../FirebaseContext';

const { Panel } = Collapse;

const Verifikacja = () => {
  /* ---------------------------------------------------------------------
   * Firebase helpers
   * -------------------------------------------------------------------*/
  const { currentFirebase } = useFirebase();
  const firestoreInstance = currentFirebase.firestore;
  const storage = getStorage(currentFirebase.app);

  /* ---------------------------------------------------------------------
   * React state
   * -------------------------------------------------------------------*/
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [modalData, setModalData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageModal, setImageModal] = useState({ visible: false, src: '' });
  const [isProjectChanging, setIsProjectChanging] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  // edit‑mode toggles for every panel
  const [editMode, setEditMode] = useState({
    basic: false,
    personal: false,
    car: false,
    tax: false,
    extra: false,
  });

  /* ---------------------------------------------------------------------
   * Ant Form instances (one per panel)
   * -------------------------------------------------------------------*/
const [basicForm] = Form.useForm();
  const [personalForm] = Form.useForm();
  const [carForm] = Form.useForm();
  const [taxForm] = Form.useForm();
  const [extraForm] = Form.useForm();

  /* ---------------------------------------------------------------------
   * Effect: load users awaiting verification
   * -------------------------------------------------------------------*/
  useEffect(() => {
    const fetchUsers = async () => {
      const startTime = Date.now();
      if (!firstLoad) setIsProjectChanging(true);

      const q = query(
        collection(firestoreInstance, 'users'),
        where('status', '==', 'Oczekuje na weryfikacje')
      );
      const qs = await getDocs(q);
      setUsers(qs.docs.map((d) => ({ id: d.id, ...d.data() })));

      if (!firstLoad) {
        const elapsed = Date.now() - startTime;
        setTimeout(() => setIsProjectChanging(false), Math.max(0, 1000 - elapsed));
      } else {
        setFirstLoad(false);
      }
    };

    fetchUsers();
  }, [firestoreInstance, firstLoad]);

  /* ---------------------------------------------------------------------
   * Helpers: Firebase read / write
   * -------------------------------------------------------------------*/
  const uploadFile = async (file, path) => {
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    return await getDownloadURL(ref);
  };

  const loadDocData = async (pathArr) => {
    const ref = doc(firestoreInstance, ...pathArr);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : {};
  };

  const loadBasicUserData = (uid) => loadDocData(['users', uid]);
  const loadDanePersonalne = (uid) => loadDocData(['users', uid, 'dokumenty', 'dane_personalne']);
  const loadSamochod = (uid) => loadDocData(['users', uid, 'dokumenty', 'samochod']);
  const loadPodatki = (uid) => loadDocData(['users', uid, 'dokumenty', 'dane_podatkowe']);
  const loadDaneDodatkowe = (uid) => loadDocData(['users', uid, 'dokumenty', 'dane_dodatkowe']);



  /* ---------------------------------------------------------------------
   * Modal open / close
   * -------------------------------------------------------------------*/
  const openModal = async (uid) => {
    setSelectedUserId(uid);
    setIsModalOpen(true);

    const [basicData, danePersonalne, samochod, podatki, daneDodatkowe] = await Promise.all([
      loadBasicUserData(uid),
      loadDanePersonalne(uid),
      loadSamochod(uid),
      loadPodatki(uid),
      loadDaneDodatkowe(uid),
    ]);

    setModalData({ basicData, danePersonalne, samochod, podatki, daneDodatkowe });

    // инициализируем формы
    basicForm.setFieldsValue(basicData);
    personalForm.setFieldsValue({ ...danePersonalne, dob: toPickerDate(danePersonalne?.dob, 'YYYY-MM-DD') });
    carForm.setFieldsValue(samochod);
    taxForm.setFieldsValue(podatki);
    extraForm.setFieldsValue(daneDodatkowe);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData({});
    setEditMode({ basic: false, personal: false, car: false, tax: false, extra: false });
  };

  /* ---------------------------------------------------------------------
   * Image preview
   * -------------------------------------------------------------------*/
  const openImageModal = (src) => setImageModal({ visible: true, src });
  const closeImageModal = () => setImageModal({ visible: false, src: '' });

  /* ---------------------------------------------------------------------
   * Verification actions
   * -------------------------------------------------------------------*/
  const approveUser = async (uid) => {
    await updateDoc(doc(firestoreInstance, 'users', uid), { status: 'Oczekuje na podpis dokumentów' });
    message.success('Użytkownik zweryfikowany (status: Oczekuje na podpis dokumentów)');
    closeModal();
  };

  const activateUser = async (uid) => {
    try {
      await updateDoc(doc(firestoreInstance, 'users', uid), { status: 'Konto Aktywne' });
      message.success('Konto aktywowane');
      closeModal();
    } catch (err) {
      message.error('Błąd aktywacji');
    }
  };
// ↓ сразу под approveUser / activateUser
const reverifyUser = async (uid) => {
  try {
    await updateDoc(doc(firestoreInstance, 'users', uid), {
      status: 'Wymagana weryfikacja KYC',
    });
    message.success('Ustawiono status: Weryfikacja KYC');
    closeModal();
  } catch (e) {
    message.error('Nie udało się ustawić ponownej weryfikacji');
  }
};
  /* ---------------------------------------------------------------------
   * Generic save handler for panels
   * -------------------------------------------------------------------*/
  const savePanel = async (values, panel) => {
    const map = {
      basic: { path: ['users', selectedUserId] },
      personal: { path: ['users', selectedUserId, 'dokumenty', 'dane_personalne'] },
      car: { path: ['users', selectedUserId, 'dokumenty', 'samochod'] },
      tax: { path: ['users', selectedUserId, 'dokumenty', 'dane_podatkowe'] },
      extra: { path: ['users', selectedUserId, 'dokumenty', 'dane_dodatkowe'] },
    };
    const payload = isPickerDate(values.dob) ? { ...values, dob: values.dob.format('YYYY-MM-DD') } : values;

    try {
      await setDoc(doc(firestoreInstance, ...map[panel].path), payload, { merge: true });
      message.success('Dane zapisane');
      setModalData((prev) => ({ ...prev, [panel === 'basic' ? 'basicData' : panel === 'personal' ? 'danePersonalne' : panel === 'car' ? 'samochod' : panel === 'tax' ? 'podatki' : 'daneDodatkowe']: { ...prev[panel], ...payload } }));
      setEditMode((m) => ({ ...m, [panel]: false }));
    } catch (err) {
      message.error('Nie udało się zapisać zmian');
    }
  };

  /* ---------------------------------------------------------------------
   * Ant Upload helper (returns props)
   * -------------------------------------------------------------------*/
  const uploadFieldProps = (form, field, folder) => ({
    listType: 'text',
    showUploadList: false,
    beforeUpload: () => false,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const url = await uploadFile(
          file,
          `users/${selectedUserId}/${folder}/${field}.${file.type.split('/')[1]}`
        );
        form.setFieldValue(field, url);
        onSuccess('ok');
      } catch (e) {
        onError(e);
      }
    },
  });

  /* ---------------------------------------------------------------------
   * Table columns
   * -------------------------------------------------------------------*/
  const columns = [
    { title: 'ID Kierowcy', dataIndex: 'driverId', key: 'driverId' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Imię', dataIndex: 'firstName', key: 'firstName' },
    { title: 'Nazwisko', dataIndex: 'lastName', key: 'lastName' },
    { title: 'Telefon', dataIndex: 'phoneNumber', key: 'phone' },
    { title: 'Rola', dataIndex: 'role', key: 'role' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Weryfikacja',
      key: 'action',
      render: (_, record) => (
        <Button type="primary" onClick={() => openModal(record.id)}>
          Weryfikacja
        </Button>
      ),
    },
  ];

  /* ---------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------*/
  return (
    <div>
      <h2>Użytkownicy oczekujący na weryfikację</h2>
      <Table columns={columns} dataSource={users} rowKey={(r) => r.id} />

      {/* =================== MAIN MODAL ======================= */}
<Modal
  title={
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>Weryfikacja Użytkownika</span>

      {/* ── кнопка справа ── */}
      <Button size="small" onClick={() => reverifyUser(selectedUserId)} style={{ marginRight: 25 }}>
        Ponowna weryfikacja
      </Button>
    </div>
  }
  open={isModalOpen}
        onCancel={closeModal}
        width={900}
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Zamknij
          </Button>,
          <Button key="verify" type="primary" onClick={() => approveUser(selectedUserId)}>
            Weryfikuj
          </Button>,
          <Button
            key="verifyActivate"
            type="primary"
            danger
            onClick={() => activateUser(selectedUserId)}
          >
            Weryfikuj & Aktywuj Konto
          </Button>,
        ]}
      >
        <Collapse accordion>
          {/* ------------------------------------------------------------------
            PANEL 1 – BASIC DATA
          ------------------------------------------------------------------ */}
          <Panel
            header="Podstawowe dane użytkownika"
            key="1"
            extra={
              <Button
                type="link"
                onClick={() => setEditMode((m) => ({ ...m, basic: !m.basic }))}
              >
                {editMode.basic ? 'Anuluj' : 'Edytuj'}
              </Button>
            }
          >
            {editMode.basic ? (
              <Form
                form={basicForm}
                layout="vertical"
                onFinish={(v) => savePanel(v, 'basic')}
                initialValues={modalData.basicData}
              >
                <Form.Item label="Obywatelstwo" name="citizenship">
                  <Input />
                </Form.Item>
                <Form.Item label="Kraj" name="country">
                  <Input />
                </Form.Item>
                <Form.Item label="ID Kierowcy" name="driverId">
                  <Input />
                </Form.Item>
                <Form.Item label="Email" name="email">
                  <Input />
                </Form.Item>
                <Form.Item label="Imię" name="firstName">
                  <Input />
                </Form.Item>
                <Form.Item label="Nazwisko" name="lastName">
                  <Input />
                </Form.Item>
                <Form.Item label="Okres rozliczeniowy" name="okresrozliczeniowy">
                  <Input />
                </Form.Item>
                <Form.Item label="Telefon" name="phoneNumber">
                  <Input />
                </Form.Item>
                <Form.Item label="Rola" name="role">
                  <Input />
                </Form.Item>
                <Form.Item label="Status" name="status">
                  <Input />
                </Form.Item>
                <Form.Item label="ID Sumsub" name="sumsubApplicantId">
                  <Input />
                </Form.Item>
                <Button htmlType="submit" type="primary">
                  Zapisz
                </Button>
              </Form>
            ) : (
              <>
                <p>Obywatelstwo: {modalData.basicData?.citizenship || 'Brak danych'}</p>
                <p>Kraj: {modalData.basicData?.country || 'Brak danych'}</p>
                <p>ID Kierowcy: {modalData.basicData?.driverId || 'Brak danych'}</p>
                <p>Email: {modalData.basicData?.email || 'Brak danych'}</p>
                <p>Imię: {modalData.basicData?.firstName || 'Brak danych'}</p>
                <p>Nazwisko: {modalData.basicData?.lastName || 'Brak danych'}</p>
                <p>
                  Okres rozliczeniowy: {modalData.basicData?.okresrozliczeniowy || 'Brak danych'}
                </p>
                <p>Telefon: {modalData.basicData?.phoneNumber || 'Brak danych'}</p>
                <p>
                  Data rejestracji:{' '}
                  {modalData.basicData?.registeredAt
                    ? new Date(modalData.basicData.registeredAt.seconds * 1000).toLocaleString()
                    : 'Brak danych'}
                </p>
                <p>Rola: {modalData.basicData?.role || 'Brak danych'}</p>
                <p>Status: {modalData.basicData?.status || 'Brak danych'}</p>
                <p>ID Sumsub: {modalData.basicData?.sumsubApplicantId || 'Brak danych'}</p>
              </>
            )}
          </Panel>

          {/* ------------------------------------------------------------------
            PANEL 2 – PERSONAL
          ------------------------------------------------------------------ */}
          <Panel
            header="Dane Personalne"
            key="2"
            extra={
              <Button
                type="link"
                onClick={() => setEditMode((m) => ({ ...m, personal: !m.personal }))}
              >
                {editMode.personal ? 'Anuluj' : 'Edytuj'}
              </Button>
            }
          >
            {editMode.personal ? (
              <Form
                form={personalForm}
                layout="vertical"
                onFinish={(v) => savePanel(v, 'personal')}
                initialValues={{ ...modalData.danePersonalne, dob: toPickerDate(modalData.danePersonalne?.dob, 'YYYY-MM-DD') }}
              >
                <Form.Item label="Obywatelstwo" name="citizenship">
                  <Input />
                </Form.Item>
                <Form.Item label="Miasto" name="city">
                  <Input />
                </Form.Item>
                <Form.Item label="Adres korespondencyjny" name="correspondenceAddress">
                  <Input />
                </Form.Item>
                <Form.Item label="Data urodzenia" name="dob">
                  <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="PESEL" name="pesel">
                  <Input />
                </Form.Item>
                <Form.Item label="Kod pocztowy" name="postalCode">
                  <Input />
                </Form.Item>
                <Form.Item label="Adres rejestracji" name="registrationAddress">
                  <Input />
                </Form.Item>
                <Form.Item label="Adres zamieszkania" name="residenceAddress">
                  <Input />
                </Form.Item>
                <Form.Item label="Ulica" name="street">
                  <Input />
                </Form.Item>
                <Button htmlType="submit" type="primary">
                  Zapisz
                </Button>
              </Form>
            ) : (
              <>
                <p>Obywatelstwo: {modalData.danePersonalne?.citizenship || 'Brak danych'}</p>
                <p>Miasto: {modalData.danePersonalne?.city || 'Brak danych'}</p>
                <p>
                  Adres korespondencyjny: {modalData.danePersonalne?.correspondenceAddress || 'Brak danych'}
                </p>
                <p>Data urodzenia: {modalData.danePersonalne?.dob || 'Brak danych'}</p>
                <p>PESEL: {modalData.danePersonalne?.pesel || 'Brak danych'}</p>
                <p>Kod pocztowy: {modalData.danePersonalne?.postalCode || 'Brak danych'}</p>
                <p>
                  Adres rejestracji: {modalData.danePersonalne?.registrationAddress || 'Brak danych'}
                </p>
                <p>
                  Adres zamieszkania: {modalData.danePersonalne?.residenceAddress || 'Brak danych'}
                </p>
                <p>Ulica: {modalData.danePersonalne?.street || 'Brak danych'}</p>
              </>
            )}
          </Panel>

          {/* ------------------------------------------------------------------
            PANEL 3 – CAR
          ------------------------------------------------------------------ */}
          <Panel
            header="Samochód"
            key="3"
            extra={
              <Button
                type="link"
                onClick={() => setEditMode((m) => ({ ...m, car: !m.car }))}
              >
                {editMode.car ? 'Anулuj' : 'Edytuj'}
              </Button>
            }
          >
            {editMode.car ? (
              <Form
                form={carForm}
                layout="vertical"
                onFinish={(v) => savePanel(v, 'car')}
                initialValues={modalData.samochod}
              >
                <Form.Item label="Producent" name="carMake">
                  <Input />
                </Form.Item>
                <Form.Item label="Model" name="carModel">
                  <Input />
                </Form.Item>
                <Form.Item label="Jakim autem" name="carOwnership">
                  <Input />
                </Form.Item>
                <Form.Item label="Tablice rejestracyjne" name="carRegistrationNumber">
                  <Input />
                </Form.Item>
                <Form.Item label="VIN" name="carVin">
                  <Input />
                </Form.Item>
                <Form.Item label="Rok" name="carYear">
                  <Input />
                </Form.Item>

                {/* ----- File fields ----- */}
                <Form.Item label="Dowód rejestracyjny (przód)" name="dowodPrzod">
                  <Upload {...uploadFieldProps(carForm, 'dowodPrzod', 'samochod')}>
                    <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
                  </Upload>
                </Form.Item>
                <Form.Item label="Dowód rejestracyjny (tył)" name="dowodTyl">
                  <Upload {...uploadFieldProps(carForm, 'dowodTyl', 'samochod')}>
                    <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
                  </Upload>
                </Form.Item>
                <Form.Item label="Ubezpieczenie" name="ubezpieczenie">
                  <Upload {...uploadFieldProps(carForm, 'ubezpieczenie', 'samochod')}>
                    <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
                  </Upload>
                </Form.Item>
                <Button htmlType="submit" type="primary">
                  Zapisz
                </Button>
              </Form>
            ) : (
              <>
                <p>Producent: {modalData.samochod?.carMake || 'Brak danych'}</p>
                <p>Model: {modalData.samochod?.carModel || 'Brak danych'}</p>
                <p>Jakim autem: {modalData.samochod?.carOwnership || 'Brak danych'}</p>
                <p>
                  Tablice rejestracyjne: {modalData.samochod?.carRegistrationNumber || 'Brak danych'}
                </p>
                <p>VIN: {modalData.samochod?.carVin || 'Brak danych'}</p>
                <p>Rok: {modalData.samochod?.carYear || 'Brak danych'}</p>
                <p>
                  Dowód przód:{' '}
                  {modalData.samochod?.dowodPrzod ? (
                    <Button type="link" onClick={() => openImageModal(modalData.samochod.dowodPrzod)}>
                      Zobacz dokument
                    </Button>
                  ) : (
                    'Brak danych'
                  )}
                </p>
                <p>
                  Dowód tył:{' '}
                  {modalData.samochod?.dowodTyl ? (
                    <Button type="link" onClick={() => openImageModal(modalData.samochod.dowodTyl)}>
                      Zobacz dokument
                    </Button>
                  ) : (
                    'Brak danych'
                  )}
                </p>
                <p>
                  Ubezpieczenie:{' '}
                  {modalData.samochod?.ubezpieczenie ? (
                    <Button type="link" onClick={() => openImageModal(modalData.samochod.ubezpieczenie)}>
                      Zobacz dokument
                    </Button>
                  ) : (
                    'Brak danych'
                  )}
                </p>
              </>
            )}
          </Panel>

          {/* ------------------------------------------------------------------
            PANEL 4 – TAX
          ------------------------------------------------------------------ */}
          <Panel
            header="Dane Podatkowe"
            key="4"
            extra={
              <Button
                type="link"
                onClick={() => setEditMode((m) => ({ ...m, tax: !m.tax }))}
              >
                {editMode.tax ? 'Anuluj' : 'Edytuj'}
              </Button>
            }
          >
            {editMode.tax ? (
              <Form
                form={taxForm}
                layout="vertical"
                onFinish={(v) => savePanel(v, 'tax')}
                initialValues={modalData.podatki}
              >
                <Form.Item label="Urząd Skarbowy" name="urzadSkarbowy">
                  <Input />
                </Form.Item>
                <Form.Item label="Rachunek Bankowy" name="rachunekBankowy">
                  <Input />
                </Form.Item>
                <Button htmlType="submit" type="primary">
                  Zapisz
                </Button>
              </Form>
            ) : (
              <>
                <p>Urząd Skarbowy: {modalData.podatki?.urzadSkarbowy || 'Brak danych'}</p>
                <p>Rachunek Bankowy: {modalData.podatki?.rachunekBankowy || 'Brak danych'}</p>
              </>
            )}
          </Panel>

          {/* ------------------------------------------------------------------
            PANEL 5 – EXTRA
          ------------------------------------------------------------------ */}
          <Panel
            header="Dane Dodatkowe"
            key="5"
            extra={
              <Button
                type="link"
                onClick={() => setEditMode((m) => ({ ...m, extra: !m.extra }))}
              >
                {editMode.extra ? 'Anuluj' : 'Edytuj'}
              </Button>
            }
          >
            {editMode.extra ? (
              <Form
                form={extraForm}
                layout="vertical"
                onFinish={(v) => savePanel(v, 'extra')}
                initialValues={modalData.daneDodatkowe}
              >
                {/* ------------- File helpers ------------- */}
                {[
                  { label: 'Badania Lekarskie', field: 'badaniaLekarskie' },
                  { label: 'Badania Psychologiczne', field: 'badaniaPsychologiczne' },
                  { label: 'Dowód Osobisty Przód', field: 'dowodOsobistyPrzod' },
                  { label: 'Dowód Osobisty Tył', field: 'dowodOsobistyTyl' },
                  { label: 'Niekaralność', field: 'niekaralnosc' },
                  { label: 'Zdjęcie Profilowe', field: 'zdjecieProfilowe' },
                ].map(({ label, field }) => (
                  <Form.Item label={label} name={field} key={field}>
                    <Upload {...uploadFieldProps(extraForm, field, 'dane_dodatkowe')}>
                      <Button icon={<UploadOutlined />}>Wgraj / Zmień</Button>
                    </Upload>
                  </Form.Item>
                ))}
                <Button htmlType="submit" type="primary">
                  Zapisz
                </Button>
              </Form>
            ) : (
              <>
                {[
                  { label: 'Badania Lekarskie', field: 'badaniaLekarskie' },
                  { label: 'Badania Psychologiczne', field: 'badaniaPsychologiczne' },
                  { label: 'Dowód Osobisty Przód', field: 'dowodOsobistyPrzod' },
                  { label: 'Dowód Osobisty Tył', field: 'dowodOsobistyTyl' },
                  { label: 'Niekaralność', field: 'niekaralnosc' },
                  { label: 'Zdjęcie Profilowe', field: 'zdjecieProfilowe' },
                ].map(({ label, field }) => (
                  <p key={field}>
                    {label}:{' '}
                    {modalData.daneDodatkowe?.[field] ? (
                      <Button type="link" onClick={() => openImageModal(modalData.daneDodatkowe[field])}>
                        Zobacz dokument
                      </Button>
                    ) : (
                      'Brak danych'
                    )}
                  </p>
                ))}
              </>
            )}
          </Panel>
        </Collapse>
      </Modal>

      {/* ================= IMAGE PREVIEW ================= */}
      <Modal open={imageModal.visible} footer={null} onCancel={closeImageModal} width={600}>
        <img src={imageModal.src} alt="Dokument" style={{ width: '100%' }} />
      </Modal>

      {/* ================= LOADING OVERLAY (PROJECT SWITCH) ================= */}
      {isProjectChanging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'opacity 0.5s ease',
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18, color: '#333' }}>Przełączam projekt…</div>
        </div>
      )}
    </div>
  );
};

export default Verifikacja;
